import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, clearAdminToken, getAdminToken } from "@/lib/adminApi";
import { AdminCollectionPage, AdminDashboardSummary, AdminFeaturedManager, AdminStatusBadge } from "./AdminComponents";
import AdminOperationsPage from "./AdminOperationsPage";
import AdminRaceEnginePage from "./AdminRaceEnginePage";
import { adminNavItems } from "./entityConfigs";

function AdminRequireAuth() {
    const location = useLocation();
    const [state, setState] = useState({ loading: true, email: "" });

    useEffect(() => {
        const token = getAdminToken();
        if (!token) {
            setState({ loading: false, email: "" });
            return;
        }
        adminApi.session()
            .then((session) => setState({ loading: false, email: session.email }))
            .catch(() => {
                clearAdminToken();
                setState({ loading: false, email: "" });
            });
    }, []);

    if (state.loading) {
        return (
            <div className="grid min-h-screen place-items-center bg-[#090c10] text-white">
                <div className="rounded-2xl border border-white/10 bg-[#11151b] px-6 py-5 text-sm text-zinc-300">
                    Checking admin session…
                </div>
            </div>
        );
    }

    if (!state.email) {
        return <Navigate to="/admin/login" replace state={{ from: location }} />;
    }

    return <Outlet context={state} />;
}

function AdminLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [session, setSession] = useState({ email: "" });

    useEffect(() => {
        adminApi.session().then((response) => setSession({ email: response.email })).catch(() => {});
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-[#090c10] text-white">
            <div className="mx-auto grid min-h-screen max-w-[1600px] md:grid-cols-[260px_minmax(0,1fr)]">
                <aside className="border-b border-white/10 bg-[#0d1217] p-4 md:border-b-0 md:border-r">
                    <div className="flex flex-col gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">Outlapped</p>
                            <h1 className="mt-2 font-display text-4xl tracking-tight text-white">Admin</h1>
                            <p className="mt-2 text-sm text-zinc-400">
                                Internal content controls for news, drivers, teams, schedule, records, and glossary.
                            </p>
                        </div>
                        <nav className="flex gap-2 overflow-x-auto md:flex-col">
                            {adminNavItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/admin"}
                                    className={({ isActive }) =>
                                        `whitespace-nowrap rounded-xl px-4 py-3 text-sm font-medium transition ${
                                            isActive
                                                ? "bg-[#1a2632] text-white ring-1 ring-[#36506a]"
                                                : "bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06]"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                </aside>
                <div className="flex min-w-0 flex-col">
                    <header className="border-b border-white/10 bg-[#0d1217]/90 px-4 py-4 backdrop-blur md:px-8">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                                    Internal Dashboard
                                </p>
                                <h2 className="mt-1 font-display text-4xl tracking-tight">
                                    {adminNavItems.find((item) => item.to === location.pathname)?.label || "Admin"}
                                </h2>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-zinc-300">
                                    {session.email}
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={async () => {
                                        await adminApi.logout();
                                        navigate("/admin/login", { replace: true });
                                    }}
                                >
                                    Log Out
                                </Button>
                            </div>
                        </div>
                    </header>
                    <main className="flex-1 px-4 py-6 md:px-8">
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
}

function AdminLoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("admin@outlapped.local");
    const [password, setPassword] = useState("admin123");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const from = location.state?.from?.pathname || "/admin";

    useEffect(() => {
        if (getAdminToken()) {
            adminApi.session().then(() => navigate("/admin", { replace: true })).catch(() => {});
        }
    }, [navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError("");
        try {
            await adminApi.login({ email, password });
            navigate(from, { replace: true });
        } catch (loginError) {
            setError(loginError?.response?.data?.detail || "Unable to sign in.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-[#090c10] px-4 text-white">
            <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#11151b] p-8 shadow-2xl">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">Outlapped Admin</p>
                <h1 className="mt-3 font-display text-5xl tracking-tight">Sign in</h1>
                <p className="mt-3 text-sm leading-6 text-zinc-400">
                    Sign in with the locally seeded admin user or your Mongo-backed admin account.
                </p>
                <form className="mt-8 flex flex-col gap-4" onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-200">Admin Email</label>
                        <Input value={email} onChange={(event) => setEmail(event.target.value)} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm text-zinc-200">Password</label>
                        <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
                    </div>
                    {error ? (
                        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                            {error}
                        </div>
                    ) : null}
                    <Button type="submit" disabled={loading}>
                        {loading ? "Signing In..." : "Enter Admin Dashboard"}
                    </Button>
                </form>
            </div>
        </div>
    );
}

function AdminDashboardPage() {
    return <AdminDashboardSummary />;
}

function AdminSettingsPage() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminApi.settings.get().then(setSettings).finally(() => setLoading(false));
    }, []);

    const updateField = (name, value) =>
        setSettings((current) => ({
            ...(current || {}),
            [name]: value,
        }));

    const saveSettings = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const next = await adminApi.settings.update(settings);
            setSettings(next);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <div className="flex flex-col gap-2">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Admin Settings</span>
                    <h1 className="font-display text-4xl tracking-tight">Platform controls</h1>
                    <p className="max-w-2xl text-sm text-zinc-400">
                        Update the admin-ready site settings and manage homepage featured content.
                    </p>
                </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                {loading ? (
                    <div className="rounded-xl border border-white/10 px-4 py-8 text-center text-zinc-400">
                        Loading settings…
                    </div>
                ) : (
                    <form className="flex flex-col gap-4" onSubmit={saveSettings}>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-zinc-100">Site Name</label>
                                <Input value={settings?.site_name || ""} onChange={(event) => updateField("site_name", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-zinc-100">Admin Email</label>
                                <Input value={settings?.admin_email || ""} onChange={(event) => updateField("admin_email", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-zinc-100">Auth Mode</label>
                                <Input value={settings?.auth_mode || ""} onChange={(event) => updateField("auth_mode", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-zinc-100">Homepage Mode</label>
                                <Input value={settings?.homepage_mode || ""} onChange={(event) => updateField("homepage_mode", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2 md:col-span-2">
                                <label className="text-sm font-medium text-zinc-100">Notes</label>
                                <Textarea value={settings?.notes || ""} onChange={(event) => updateField("notes", event.target.value)} rows={4} />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button type="submit" disabled={saving}>
                                {saving ? "Saving..." : "Save Settings"}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
            <AdminFeaturedManager />
        </div>
    );
}

function AdminProvidersPage() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({});
    const providerCopy = {
        openf1: "Disabled for MVP / future live telemetry.",
        api_sports: "Optional structured data sync for drivers, teams, schedule, and standings.",
        warehouse: "Historical replay, processed telemetry, Race IQ, and post-race analytics only.",
        local_json: "Fallback, seed, import/export backup, and development safety net.",
        mock: "Local development provider for non-production testing only.",
    };

    const loadProviders = () =>
        adminApi.providers.list().then((items) => {
            setProviders(items);
            setDrafts(
                Object.fromEntries(
                    items.map((item) => [
                        item.provider_name,
                        {
                            enabled: Boolean(item.enabled),
                            priority: item.priority ?? 0,
                            base_url: item.base_url || "",
                            api_key: "",
                        },
                    ])
                )
            );
        }).finally(() => setLoading(false));

    useEffect(() => {
        loadProviders();
    }, []);

    const updateDraft = (providerName, field, value) =>
        setDrafts((current) => ({
            ...current,
            [providerName]: { ...(current[providerName] || {}), [field]: value },
        }));

    const saveProvider = async (providerName) => {
        const draft = drafts[providerName] || {};
        await adminApi.providers.update(providerName, draft);
        await loadProviders();
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Provider Settings</span>
                <h1 className="mt-2 font-display text-4xl tracking-tight">Providers</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-400">
                    Configure provider priority, enablement, base URLs, and encrypted key storage status.
                </p>
                <p className="mt-3 max-w-3xl text-sm text-zinc-500">
                    MongoDB is the primary database/source of truth. Providers feed data into MongoDB or provide fallback/replay data.
                </p>
            </div>
            <div className="grid gap-4">
                {loading ? (
                    <div className="rounded-2xl border border-white/10 bg-[#11151b] px-4 py-8 text-center text-zinc-400">Loading providers…</div>
                ) : (
                    providers.map((provider) => (
                        <div key={provider.provider_name} className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                                <div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <h2 className="font-display text-3xl tracking-tight">{provider.provider_name}</h2>
                                        <AdminStatusBadge value={provider.enabled ? "active" : "inactive"} />
                                    </div>
                                    <p className="mt-2 text-sm text-zinc-300">
                                        {providerCopy[provider.provider_name] || provider.api_key_status}
                                    </p>
                                    <p className="mt-1 text-sm text-zinc-500">
                                        {provider.api_key_status}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-500">
                                        <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
                                            Mode: {provider.config?.mode || "manual"}
                                        </span>
                                        {(provider.usage || []).map((entry) => (
                                            <span key={entry} className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
                                                {entry}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                                        <label className="flex flex-col gap-2 text-sm">
                                            <span className="text-zinc-300">Enabled</span>
                                            <select
                                                className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-white"
                                                value={drafts[provider.provider_name]?.enabled ? "yes" : "no"}
                                                onChange={(event) => updateDraft(provider.provider_name, "enabled", event.target.value === "yes")}
                                            >
                                                <option value="yes">Enabled</option>
                                                <option value="no">Disabled</option>
                                            </select>
                                        </label>
                                        <label className="flex flex-col gap-2 text-sm">
                                            <span className="text-zinc-300">Priority</span>
                                            <Input
                                                value={drafts[provider.provider_name]?.priority ?? ""}
                                                onChange={(event) => updateDraft(provider.provider_name, "priority", Number(event.target.value || 0))}
                                            />
                                        </label>
                                        <label className="flex flex-col gap-2 text-sm">
                                            <span className="text-zinc-300">Base URL</span>
                                            <Input
                                                value={drafts[provider.provider_name]?.base_url ?? ""}
                                                onChange={(event) => updateDraft(provider.provider_name, "base_url", event.target.value)}
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Secrets</p>
                                    <label className="mt-4 flex flex-col gap-2 text-sm">
                                        <span className="text-zinc-300">Replace stored API key</span>
                                        <Input
                                            type="password"
                                            value={drafts[provider.provider_name]?.api_key ?? ""}
                                            onChange={(event) => updateDraft(provider.provider_name, "api_key", event.target.value)}
                                            placeholder="Only saved if encryption is configured"
                                        />
                                    </label>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <Button type="button" onClick={() => saveProvider(provider.provider_name)}>
                                            Save Provider
                                        </Button>
                                        <Button type="button" variant="outline" onClick={() => adminApi.providers.deleteKey(provider.provider_name).then(loadProviders)}>
                                            Delete Stored Key
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function AdminSyncPage() {
    const [logs, setLogs] = useState([]);
    const [busy, setBusy] = useState("");

    const loadLogs = () => adminApi.sync.logs().then(setLogs);

    useEffect(() => {
        loadLogs();
    }, []);

    const run = async (key, fn) => {
        setBusy(key);
        try {
            await fn();
            await loadLogs();
        } finally {
            setBusy("");
        }
    };

    const actions = [
        ["schedule", "Sync Schedule", adminApi.sync.schedule],
        ["drivers", "Sync Drivers", adminApi.sync.drivers],
        ["teams", "Sync Teams", adminApi.sync.teams],
        ["standings", "Sync Standings", adminApi.sync.standings],
        ["news", "Sync News", adminApi.sync.news],
        ["all", "Sync All", adminApi.sync.all],
        ["stats", "Recompute Stats", adminApi.sync.recomputeStats],
    ];

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Sync Control</span>
                <h1 className="mt-2 font-display text-4xl tracking-tight">Sync</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-400">
                    Run provider syncs manually and review the latest sync log entries.
                </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {actions.map(([key, label, fn]) => (
                    <Button key={key} type="button" variant="outline" disabled={busy === key} onClick={() => run(key, fn)}>
                        {busy === key ? "Running…" : label}
                    </Button>
                ))}
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-4">
                <h2 className="font-display text-3xl tracking-tight">Recent Sync Logs</h2>
                <div className="mt-4 divide-y divide-white/10">
                    {logs.length ? (
                        logs.map((log) => (
                            <div key={log.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                                <div>
                                    <p className="font-semibold text-white">{log.sync_type}</p>
                                    <p className="text-zinc-500">{log.provider_name}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <AdminStatusBadge value={log.status} />
                                    <span className="text-zinc-500">{log.finished_at || log.started_at || "—"}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-8 text-center text-zinc-500">No sync logs yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function AdminAuditLogsPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.auditLogs().then(setLogs).finally(() => setLoading(false));
    }, []);

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Audit Trail</span>
                <h1 className="mt-2 font-display text-4xl tracking-tight">Audit Logs</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-400">
                    Recent admin actions against editable content and provider configuration.
                </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-4">
                {loading ? (
                    <div className="px-4 py-8 text-center text-zinc-400">Loading audit logs…</div>
                ) : logs.length ? (
                    <div className="divide-y divide-white/10">
                        {logs.map((log) => (
                            <div key={log.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                                <div>
                                    <p className="font-semibold text-white">{log.action}</p>
                                    <p className="text-zinc-500">{log.collection} · {log.item_id}</p>
                                </div>
                                <div className="text-right text-zinc-500">
                                    <p>{log.actor_email}</p>
                                    <p>{log.created_at}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="px-4 py-8 text-center text-zinc-500">No audit logs yet.</div>
                )}
            </div>
        </div>
    );
}

export function AdminRoutes() {
    return (
        <Routes>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminRequireAuth />}>
                <Route element={<AdminLayout />}>
                    <Route index element={<AdminDashboardPage />} />
                    <Route path="news" element={<AdminCollectionPage entityKey="news" />} />
                    <Route path="drivers" element={<AdminCollectionPage entityKey="drivers" />} />
                    <Route path="teams" element={<AdminCollectionPage entityKey="teams" />} />
                    <Route path="calendar" element={<AdminCollectionPage entityKey="calendar" />} />
                    <Route path="stats" element={<AdminCollectionPage entityKey="records" />} />
                    <Route path="records" element={<AdminCollectionPage entityKey="records" />} />
                    <Route path="glossary" element={<AdminCollectionPage entityKey="glossary" />} />
                    <Route path="featured" element={<AdminFeaturedManager />} />
                    <Route path="providers" element={<AdminProvidersPage />} />
                    <Route path="operations" element={<AdminOperationsPage />} />
                    <Route path="race-engine" element={<AdminRaceEnginePage />} />
                    <Route path="sync" element={<AdminSyncPage />} />
                    <Route path="audit-logs" element={<AdminAuditLogsPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                </Route>
            </Route>
        </Routes>
    );
}
